// Drone map overlays (§228) — upload an image orthomosaic, place it by drawing its footprint
// on a satellite preview, set opacity, and toggle it on the farm map. v1 = raster image (PNG/
// JPG); the image is client-downscaled to fit the 2 MB request cap (an overlay doesn't need
// full res). GeoTIFF auto-georeferencing is a follow-up. Placement uses leaflet-draw's rectangle.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plane, Upload, Trash2, Crosshair } from 'lucide-react'
import { droneMapsApi, type DroneMap } from '../lib/api'
import { apiError } from '../lib/errors'
import { isGeoTIFF, parseGeoTIFF, type GeoBounds } from '../lib/geotiff'

const ESRI = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

// prepareImage downscales an image to fit ~1.9 MB (canvas → JPEG). Big orthos lose resolution
// but stay perfectly usable as an overlay; keeps the upload under the 2 MB request cap.
async function prepareImage(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file).catch(() => null)
  if (!bmp) return file
  const maxDim = 2400
  let { width, height } = bmp
  if (width > maxDim || height > maxDim) {
    const s = maxDim / Math.max(width, height)
    width = Math.round(width * s)
    height = Math.round(height * s)
  }
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, width, height)
  for (const q of [0.85, 0.7, 0.55, 0.4]) {
    const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/jpeg', q))
    if (blob && blob.size < 1_900_000) return blob
  }
  const fallback: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.3))
  return fallback || file
}

export default function DroneMapsPage() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['drone-maps'], queryFn: () => droneMapsApi.list().then(r => r.data.maps) })
  const maps = useMemo(() => data ?? [], [data])

  const mapDivRef = useRef<HTMLDivElement>(null)
  const leafletRef = useRef<any>(null)
  const overlaysRef = useRef<Record<string, any>>({})
  const urlCacheRef = useRef<Record<string, string>>({})
  const [ready, setReady] = useState(false)
  const [placingId, setPlacingId] = useState<string | null>(null)
  const placingRef = useRef<string | null>(null)
  const [err, setErr] = useState('')

  // Init the satellite preview map + leaflet-draw.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const mod = await import('leaflet')
      const L = mod.default
      ;(window as any).L = L
      await import('leaflet-draw')
      if (cancelled || !mapDivRef.current || leafletRef.current) return
      const saved = localStorage.getItem('grafty-farm-location')
      const center: [number, number] = saved ? JSON.parse(saved) : [26.65, -82.12]
      const map = L.map(mapDivRef.current, { zoomControl: true }).setView(center, 16)
      L.tileLayer(ESRI, { maxZoom: 22, maxNativeZoom: 19, attribution: 'Esri' }).addTo(map)
      leafletRef.current = map
      map.on((L as any).Draw.Event.CREATED, (e: any) => {
        const id = placingRef.current
        if (!id) return
        const b = e.layer.getBounds()
        update.mutate({ id, data: { south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() } })
        setPlacingId(null)
        placingRef.current = null
      })
      setReady(true)
    })()
    const urls = urlCacheRef.current // stable object (mutated in place, never reassigned)
    return () => {
      cancelled = true
      Object.values(urls).forEach(u => URL.revokeObjectURL(u))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Render an imageOverlay for every placed map (opacity from the row); fit to the newest.
  useEffect(() => {
    const map = leafletRef.current
    const L = (window as any).L
    if (!map || !L || !ready) return
    const live = new Set<string>()
    ;(async () => {
      for (const m of maps) {
        if (m.south == null || m.north == null) continue
        live.add(m.id)
        let url = urlCacheRef.current[m.id]
        if (!url) {
          try {
            const blob = (await droneMapsApi.file(m.id)).data
            url = URL.createObjectURL(blob)
            urlCacheRef.current[m.id] = url
          } catch { continue }
        }
        const bounds: [[number, number], [number, number]] = [[m.south, m.west!], [m.north, m.east!]]
        if (overlaysRef.current[m.id]) {
          overlaysRef.current[m.id].setBounds(bounds).setOpacity(m.opacity)
        } else {
          overlaysRef.current[m.id] = L.imageOverlay(url, bounds, { opacity: m.opacity, interactive: false }).addTo(map)
        }
      }
      // Remove overlays for maps that are gone / unplaced.
      Object.keys(overlaysRef.current).forEach(id => {
        if (!live.has(id)) {
          try { map.removeLayer(overlaysRef.current[id]) } catch { /* ignore */ }
          delete overlaysRef.current[id]
          if (urlCacheRef.current[id]) { URL.revokeObjectURL(urlCacheRef.current[id]); delete urlCacheRef.current[id] }
        }
      })
    })()
  }, [maps, ready])

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // A GeoTIFF is parsed in-browser → auto-derived bounds + a downscaled JPEG (§228). A plain
      // image is downscaled and placed by hand (draw the footprint).
      let blob: Blob
      let bounds: GeoBounds | null = null
      if (isGeoTIFF(file)) { const parsed = await parseGeoTIFF(file); blob = parsed.blob; bounds = parsed.bounds }
      else blob = await prepareImage(file)
      const fd = new FormData()
      fd.append('file', blob, 'ortho.jpg')
      fd.append('name', file.name.replace(/\.[^.]+$/, ''))
      const map = (await droneMapsApi.upload(fd)).data
      if (bounds) await droneMapsApi.update(map.id, bounds)
      return { map, bounds }
    },
    onSuccess: ({ map, bounds }) => {
      setErr('')
      qc.invalidateQueries({ queryKey: ['drone-maps'] })
      if (bounds) {
        // Auto-placed from the GeoTIFF — just show it (fit the preview to the footprint).
        try { leafletRef.current?.fitBounds([[bounds.south, bounds.west], [bounds.north, bounds.east]], { maxZoom: 19 }) } catch { /* ignore */ }
      } else {
        startPlacing(map.id)
      }
    },
    onError: (e) => setErr(apiError(e, 'Upload failed')),
  })
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DroneMap> }) => droneMapsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drone-maps'] }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => droneMapsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drone-maps'] }),
  })

  function startPlacing(id: string) {
    const L = (window as any).L
    const map = leafletRef.current
    if (!L || !map) return
    setPlacingId(id)
    placingRef.current = id
    new L.Draw.Rectangle(map, { shapeOptions: { color: '#F5A623', weight: 2 } }).enable()
  }

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Plane size={22} color="var(--color-primary)" />
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>Drone Maps</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>
        Upload a drone orthomosaic and it shows as a layer on the Farm Map. A <strong>GeoTIFF auto-places</strong>
        from its georeferencing; a plain PNG/JPG you place by drawing its footprint.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer', background: 'var(--color-primary)', color: '#fff' }}>
          <Upload size={15} /> {upload.isPending ? 'Uploading…' : 'Upload ortho'}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/tiff,.tif,.tiff" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.currentTarget.value = '' }} />
        </label>
        {placingId && <span style={{ fontSize: 12.5, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Crosshair size={14} /> Draw a rectangle over where this ortho sits on your farm.
        </span>}
      </div>
      {err && <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{err}</p>}

      <div ref={mapDivRef} style={{ height: '46vh', minHeight: 320, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--color-border)', marginBottom: 16 }} />

      {maps.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13, padding: 20 }}>No drone maps yet. Upload one above.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {maps.map(m => (
            <div key={m.id} style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>{m.name || 'Ortho'}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--color-text-faint)' }}>
                    {m.captured_on ? `captured ${m.captured_on}` : `added ${new Date(m.created_at).toLocaleDateString()}`}
                    {m.south == null && ' · not placed'}
                  </div>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                  Opacity
                  <input type="range" min={0.1} max={1} step={0.05} defaultValue={m.opacity}
                    onMouseUp={e => update.mutate({ id: m.id, data: { opacity: Number((e.target as HTMLInputElement).value) } })}
                    onTouchEnd={e => update.mutate({ id: m.id, data: { opacity: Number((e.target as HTMLInputElement).value) } })} />
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--color-text-muted)' }}>
                  <input type="checkbox" checked={m.is_active} onChange={e => update.mutate({ id: m.id, data: { is_active: e.target.checked } })} /> On farm map
                </label>
                <button onClick={() => startPlacing(m.id)} style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, borderRadius: 7, cursor: 'pointer', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)' }}>
                  {m.south == null ? 'Place' : 'Reposition'}
                </button>
                <button onClick={() => { if (confirm('Delete this drone map?')) remove.mutate(m.id) }} title="Delete"
                  style={{ padding: '6px 8px', borderRadius: 7, cursor: 'pointer', border: '1px solid var(--color-border)', background: 'transparent', color: '#dc2626' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  )
}
